import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Typography,
    Grid,
    IconButton
} from '@mui/material';
import { Add, Edit, Delete, Person, Email, Phone, LocationOn, Business, Search } from '@mui/icons-material';
import { customersApi, Customer } from '../services/customersApi';
import GenericPage from '../../Commun/components/GenericPage';
import RootDialogPage from '../../Commun/components/RootDialogPage';
import { CustomerEditDialog } from '../components/CustomerEditDialog';
import { BlobticZoomPopup } from '../../Commun/components/BlobticZoomPopup';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { blobticMessage } from '../../Commun/components/BlobticMessage';
import './Customers.css';

const Customers: React.FC = () => {
    const { t } = useLanguage();
    
    const [customers, setCustomers] = useState<Customer[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [editingCustomer, setEditingCustomer] = useState<Customer | null>(null);
    const [customerToDelete, setCustomerToDelete] = useState<Customer | null>(null);
    const [isZoomOpen, setIsZoomOpen] = useState(false);

    useEffect(() => {
        loadCustomers();
    }, []);

    const loadCustomers = async () => {
        try {
            setLoading(true);
            const response = await customersApi.getAll();
            setCustomers(response.data || []);
        } catch (error) {
            console.error('Error loading customers:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateClick = () => {
        setEditingCustomer(null);
        setIsModalOpen(true);
    };

    const handleEditClick = (customer: Customer) => {
        setEditingCustomer(customer);
        setIsModalOpen(true);
    };

    const handleDeleteClick = (customer: Customer) => {
        setCustomerToDelete(customer);
        setIsDeleteModalOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!customerToDelete) return;
        try {
            await customersApi.delete(customerToDelete.id);
            await loadCustomers();
            setIsDeleteModalOpen(false);
            setCustomerToDelete(null);
            blobticMessage.success(t('common.messages.success'), t('maker.msg.success.delete'));
        } catch (error: any) {
            blobticMessage.error(t('common.messages.error'), `Failed to delete customer: ${error.message}`);
        }
    };

    const handleZoomClick = () => {
        setIsZoomOpen(true);
    };

    const handleZoomSelect = (data: any) => {
        // Find existing customer or load it
        // The zoom returns flat data, we need to convert it or fetch by ID
        // For simplicity, we can fetch by ID using the data.CUS_IDF if available
        if (data && data.CUS_IDF) {
            // Find in current list or fetch
            const existing = customers.find(c => c.id === data.CUS_IDF);
            if (existing) {
                handleEditClick(existing);
            } else {
                // If not in current list (maybe pagination later?), fetch it
                customersApi.getById(data.CUS_IDF).then(res => {
                    if (res.success && res.data) {
                        handleEditClick(res.data);
                    }
                });
            }
        }
    };

    return (
        <GenericPage
            title={t('maker.customers.title')}
            icon={<Person />}
            footerEnabled={false}
            additionalActions={
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="outlined" startIcon={<Search />} onClick={handleZoomClick}>
                        {t('maker.customers.search')}
                    </Button>
                    <Button variant="contained" startIcon={<Add />} onClick={handleCreateClick}>
                        {t('maker.customers.new')}
                    </Button>
                </Box>
            }
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 1 }}>
                    {loading ? (
                        <Typography align="center">{t('maker.customers.loading')}</Typography>
                    ) : customers.length === 0 ? (
                        <Typography align="center" color="text.secondary" sx={{ mt: 5 }}>{t('maker.customers.empty')}</Typography>
                    ) : (
                        <Grid container spacing={3}>
                            {customers.map((customer) => (
                                <Grid item xs={12} sm={6} md={4} key={customer.id}>
                                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
                                        <CardContent>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                <Typography variant="h6" fontWeight="bold">{customer.name}</Typography>
                                                <Box>
                                                    <IconButton size="small" color="primary" onClick={() => handleEditClick(customer)}><Edit fontSize="small" /></IconButton>
                                                    <IconButton size="small" color="error" onClick={() => handleDeleteClick(customer)}><Delete fontSize="small" /></IconButton>
                                                </Box>
                                            </Box>

                                            {customer.code && (
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                                    {customer.code}
                                                </Typography>
                                            )}

                                            {customer.companyName && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1, color: 'text.secondary' }}>
                                                    <Business fontSize="small" />
                                                    <Typography variant="body2">{customer.companyName}</Typography>
                                                </Box>
                                            )}

                                            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                {customer.email && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Email fontSize="small" color="action" />
                                                        <Typography variant="body2">{customer.email}</Typography>
                                                    </Box>
                                                )}
                                                {customer.phone && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Phone fontSize="small" color="action" />
                                                        <Typography variant="body2">{customer.phone}</Typography>
                                                    </Box>
                                                )}
                                                {customer.city && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <LocationOn fontSize="small" color="action" />
                                                        <Typography variant="body2">{customer.city}, {customer.country}</Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </Box>

                <CustomerEditDialog
                    open={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSuccess={loadCustomers}
                    customer={editingCustomer}
                />

                <RootDialogPage
                    open={isDeleteModalOpen}
                    onClose={() => setIsDeleteModalOpen(false)}
                    title={t('maker.customers.delete.title')}
                    onAction={handleDeleteConfirm}
                    actionLabel={t('maker.customers.delete.action')}
                    maxWidth="xs"
                >
                    <Typography>
                        {t('maker.customers.delete.confirm', customerToDelete?.name)}
                    </Typography>
                </RootDialogPage>

                <BlobticZoomPopup
                    open={isZoomOpen}
                    zoomKey="maker_customer"
                    onClose={() => setIsZoomOpen(false)}
                    onSelect={handleZoomSelect}
                    title="maker.customers.search"
                />
            </Box>
        </GenericPage>
    );
};

export default Customers;

